import { apiRequest, apiBaseUrl, getSession } from "../api";
import type {
  AuditMemberRole,
  AuditPortalLink,
  AuditPortalQuery,
  CreateSharedEngagement,
  CreateSharedQuery,
  ReviewAuditEvidence,
  SharedAuditEngagement,
  SharedAuditQuery,
  SharedQueryAction,
  UploadAuditEvidence,
} from "./collaboration-model";

const root = "/audit-collaboration/engagements";
const engagementPath = (id: string) => `${root}/${encodeURIComponent(id)}`;
const queryPath = (engagementId: string, queryId: string) =>
  `${engagementPath(engagementId)}/queries/${encodeURIComponent(queryId)}`;
const json = <T>(path: string, method: string, value?: unknown) =>
  apiRequest<T>(path, {
    method,
    body: value === undefined ? undefined : JSON.stringify(value),
  });
export type AuditCompany = {
  id: string;
  name: string;
  financialYear: string;
  canCreate: boolean;
};
export type MemberCandidate = {
  userId: string;
  displayName: string;
  eligibleRoles: AuditMemberRole[];
};
export type PortalLinkRecord = Omit<AuditPortalLink, "token"> & {
  revokedAt?: string;
};

async function attachmentDownload(path: string, token: string, name: string) {
  const response = await fetch(`${apiBaseUrl}/api${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
    credentials: "omit",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok)
    throw new Error(
      `Evidence download failed (${response.status}). Refresh access and try again.`,
    );
  // Files are downloaded as attachments; never render uploaded content in the app.
  const blob = new Blob([await response.arrayBuffer()], {
    type: "application/octet-stream",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name.replace(/[\\/\u0000-\u001f]/g, "_");
  anchor.rel = "noreferrer";
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export const collaborationApi = {
  companies: () => apiRequest<AuditCompany[]>("/audit-collaboration/companies"),
  list: () => apiRequest<SharedAuditEngagement[]>(root),
  get: (id: string) => apiRequest<SharedAuditEngagement>(engagementPath(id)),
  create: (input: CreateSharedEngagement) =>
    json<SharedAuditEngagement>(root, "POST", input),
  candidates: (id: string) =>
    apiRequest<MemberCandidate[]>(`${engagementPath(id)}/member-candidates`),
  setMember: (id: string, userId: string, role: AuditMemberRole) =>
    json<SharedAuditEngagement>(
      `${engagementPath(id)}/members/${encodeURIComponent(userId)}`,
      "PUT",
      { role },
    ),
  removeMember: (id: string, userId: string) =>
    json<SharedAuditEngagement>(
      `${engagementPath(id)}/members/${encodeURIComponent(userId)}`,
      "DELETE",
    ),
  createQuery: (id: string, input: CreateSharedQuery) =>
    json<SharedAuditQuery>(`${engagementPath(id)}/queries`, "POST", input),
  action: (id: string, queryId: string, action: SharedQueryAction) =>
    json<SharedAuditQuery>(`${queryPath(id, queryId)}/actions`, "POST", action),
  upload: (id: string, queryId: string, evidence: UploadAuditEvidence) =>
    json<SharedAuditQuery>(
      `${queryPath(id, queryId)}/attachments`,
      "POST",
      evidence,
    ),
  review: (
    id: string,
    queryId: string,
    attachmentId: string,
    review: ReviewAuditEvidence,
  ) =>
    json<SharedAuditQuery>(
      `${queryPath(id, queryId)}/attachments/${encodeURIComponent(attachmentId)}/review`,
      "POST",
      review,
    ),
  download: (id: string, queryId: string, attachmentId: string, name: string) =>
    attachmentDownload(
      `${queryPath(id, queryId)}/attachments/${encodeURIComponent(attachmentId)}`,
      getSession()?.accessToken ?? "",
      name,
    ),
  links: (id: string, queryId: string) =>
    apiRequest<PortalLinkRecord[]>(`${queryPath(id, queryId)}/links`),
  createLink: (
    id: string,
    queryId: string,
    revision: number,
    expiresInDays: number,
  ) =>
    json<AuditPortalLink>(`${queryPath(id, queryId)}/links`, "POST", {
      revision,
      expiresInDays,
    }),
  revokeLink: (id: string, queryId: string, linkId: string, revision: number) =>
    json<SharedAuditQuery>(
      `${queryPath(id, queryId)}/links/${encodeURIComponent(linkId)}`,
      "DELETE",
      { revision },
    ),
};

async function publicRequest<T>(
  token: string,
  path = "",
  value?: unknown,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}/api/audit-portal${path}`, {
    method: value === undefined ? "GET" : "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(value === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: value === undefined ? undefined : JSON.stringify(value),
    credentials: "omit",
    cache: "no-store",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) {
    if ([401, 403, 404, 410].includes(response.status))
      throw new Error(
        "This link is unavailable or expired. Please request a new link from your audit contact.",
      );
    if (response.status === 413)
      throw new Error("The file exceeds the 5 MB upload limit.");
    if (response.status === 409)
      throw new Error(
        "This query has changed or is no longer accepting responses. Refresh the query before trying again. Your text has been kept.",
      );
    throw new Error(
      "Your request could not be completed. Please check the file or response and try again.",
    );
  }
  return response.json() as Promise<T>;
}
export const portalApi = {
  get: (token: string) => publicRequest<AuditPortalQuery>(token),
  reply: (token: string, text: string) =>
    publicRequest<AuditPortalQuery>(token, "/replies", { text }),
  upload: (token: string, file: UploadAuditEvidence) =>
    publicRequest<AuditPortalQuery>(token, "/attachments", {
      name: file.name,
      contentBase64: file.contentBase64,
    }),
  download: (token: string, id: string, name: string) =>
    attachmentDownload(
      `/audit-portal/attachments/${encodeURIComponent(id)}`,
      token,
      name,
    ),
};

export async function prepareEvidence(
  file: File,
): Promise<UploadAuditEvidence> {
  if (!file.size || file.size > 5 * 1024 * 1024)
    throw new Error("Select a non-empty PDF, PNG or JPEG no larger than 5 MB.");
  if (!/\.(pdf|png|jpe?g)$/i.test(file.name))
    throw new Error("Only PDF, PNG and JPEG evidence files are accepted.");
  const contentBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(
        new Error(
          "The selected file could not be read. Please select it again.",
        ),
      );
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
  return { name: file.name, contentBase64 };
}
